import { store } from 'redux/store';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { HoldingClass, Share, Range } from 'types';

import { getSharesByHoldingClassOrPartner } from 'modules/partners/api/getSharesByHoldingClassOrPartner';

import { checkOverlaps } from '../utils/checkOverlaps';
import { CustomRange } from '../types/CustomRange';

type UseRenumerationRangesProps = {
  holdingClassId?: string;
  partnerId?: string;
  page?: number;
  rowsPerPage?: number;
  isEntireClassRenumeration: boolean;
};

export const useRenumerationRanges = ({
  holdingClassId,
  partnerId,
  page = 1,
  rowsPerPage = 10,
  isEntireClassRenumeration,
}: UseRenumerationRangesProps) => {
  const [apiShares, setApiShares] = useState<Share[]>([]);
  const [reassignedRanges, setReassignedRanges] = useState<CustomRange[]>([]);
  const [savedRanges, setSavedRanges] = useState<CustomRange[]>([]);
  const [totalRanges, setTotalRanges] = useState(0);
  const [precedingSharesCount, setPrecedingSharesCount] = useState(0);
  const [overlaps, setOverlaps] = useState<Range[]>([]);
  const [overlapCount, setOverlapCount] = useState(0);
  const [isChecking, setIsChecking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const ranges: CustomRange[] = useMemo(() => {
    if (isEntireClassRenumeration) {
      let cursor = precedingSharesCount + 1;
      return apiShares.map((share) => {
        const holdingClass = share.shareClass as HoldingClass;

        const size = share.to - share.from + 1;
        const newFrom = cursor;
        const newTo = cursor + size - 1;
        cursor = newTo + 1;

        return {
          key: `${holdingClass?._id}-${share.from}-${share.to}`,
          from: share.from,
          to: share.to,
          shareClass: holdingClass?._id,
          partner: share?.partner as string,
          sharePremium: share.sharePremium,
          sharePrice: share.sharePrice,
          newFrom,
          newTo,
        };
      });
    }

    return apiShares.map((share) => {
      const holdingClass = share.shareClass as HoldingClass;

      const key = `${holdingClass?._id}-${share.from}-${share.to}`;
      const reassigned = reassignedRanges.find((r) => r.key === key);
      return {
        key,
        from: share.from,
        to: share.to,
        newFrom: reassigned?.newFrom,
        newTo: reassigned?.newTo,
        shareClass: holdingClass?._id,
        sharePremium: share.sharePremium,
        sharePrice: share.sharePrice,
        partner: share?.partner as string,
      };
    });
  }, [
    apiShares,
    reassignedRanges,
    isEntireClassRenumeration,
    precedingSharesCount,
  ]);

  const handleEditRange = (
    key: string,
    range: CustomRange,
    newFrom: number,
    newTo: number
  ) => {
    setOverlaps([]);
    setOverlapCount(0);

    if (newFrom === range.from && newTo === range.to) {
      setReassignedRanges((prev) => prev.filter((r) => r.key !== key));
      return;
    }

    setReassignedRanges((prev) => {
      const exists = prev.some((r) => r.key === key);
      return exists
        ? prev.map((r) => (r.key === key ? { ...r, newFrom, newTo } : r))
        : [...prev, { ...range, newFrom, newTo }];
    });
  };

  const handleResetValues = () => {
    setReassignedRanges([]);
    setOverlaps([]);
    setOverlapCount(0);
    setIsError(false);
  };

  const handleSaveRanges = async () => {
    setIsChecking(true);
    setIsError(false);

    try {
      const result = await checkOverlaps({
        holdingClassId,
        reassignedRanges,
        savedRanges,
      });

      if (result.overlaps.length > 0) {
        setOverlaps(result.overlaps);
        setOverlapCount(result.overlapCount);
        return false;
      }

      setSavedRanges((prev) => [...prev, ...reassignedRanges]);
      handleResetValues();
      return true;
    } catch (error) {
      setIsError(true);
      return false;
    } finally {
      setIsChecking(false);
    }
  };

  const fetchRanges = useCallback(async () => {
    if (!holdingClassId) {
      setApiShares([]);
      setTotalRanges(0);
      return;
    }

    setIsLoading(true);
    setIsError(false);

    try {
      const sharesData = await store.dispatch(
        getSharesByHoldingClassOrPartner({
          holdingClassId,
          partnerId,
          params: {
            page,
            items: rowsPerPage,
            fieldsToPopulate: ['shareClass'],
          },
        })
      );
      setApiShares(sharesData?.shares || []);

      setTotalRanges(sharesData?.total || 0);
      setPrecedingSharesCount(sharesData?.precedingSharesCount || 0);
    } catch (error) {
      setApiShares([]);
      setTotalRanges(0);
      setPrecedingSharesCount(0);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [holdingClassId, page, partnerId, rowsPerPage]);

  useEffect(() => {
    fetchRanges();
  }, [fetchRanges]);

  return {
    data: {
      ranges,
      totalRanges,
      reassignedRanges,
      savedRanges,
      overlaps,
      overlapCount,
    },
    actions: { handleEditRange, handleResetValues, handleSaveRanges },
    isLoading,
    isChecking,
    isError,
  };
};
